import React, { useEffect, useState } from "react";
import { Smartphone, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import WaitlistModal from "./WaitlistModal";
import { useReaderAppMode } from "../lib/readerAppMode";
import { WAITLIST_DISMISSED_KEY } from "../lib/storageKeys";

/**
 * WaitlistBanner — thin top-of-page pill banner announcing the iOS +
 * Android waitlist. Tapping it opens `WaitlistModal`. Dismissable; the
 * dismissal flag lives in localStorage so users aren't nagged every
 * visit.
 *
 * Hidden inside Capacitor reader-app mode — the iOS/Android users
 * literally ARE in the mobile app, so a "join the mobile waitlist"
 * pitch is nonsensical.
 */
const WaitlistBanner = () => {
  const { t } = useTranslation();
  const readerApp = useReaderAppMode();
  const [dismissed, setDismissed] = useState(true); // hidden by default until we check storage
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    try {
      setDismissed(Boolean(localStorage.getItem(WAITLIST_DISMISSED_KEY)));
    } catch {
      setDismissed(false);
    }
  }, []);

  // While the banner is visible, add a body class so the fixed Header
  // can shift down via CSS. Mount/unmount cleans it up.
  useEffect(() => {
    if (readerApp || dismissed) return undefined;
    document.body.classList.add("waitlist-banner-visible");
    return () => document.body.classList.remove("waitlist-banner-visible");
  }, [readerApp, dismissed]);

  if (readerApp || dismissed) return null;

  const onDismiss = (e) => {
    e.stopPropagation();
    try {
      localStorage.setItem(WAITLIST_DISMISSED_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <>
      <div
        className="fixed top-0 inset-x-0 z-[55] bg-gradient-to-r from-[#F2D9D4] via-[#EADDD7] to-[#F2D9D4] border-b border-[#E1CBCB]"
        data-testid="waitlist-banner"
      >
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="w-full text-center py-3 px-10 flex items-center justify-center gap-2 hover:bg-[#EADDD7]/50 transition-colors text-[#2C2123] text-sm"
          data-testid="waitlist-banner-open-btn"
        >
          <Smartphone className="h-4 w-4 text-[#9B6A6C]" />
          <span>
            {t("waitlistBanner.lead")}{" "}
            <span className="font-medium text-[#5D4050]">
              {t("waitlistBanner.cta")}
            </span>{" "}
            <span className="text-[#9B6A6C]">&rarr;</span>
          </span>
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t("waitlistBanner.dismissAria")}
          className="absolute top-1/2 right-3 -translate-y-1/2 p-1 rounded-full hover:bg-[#EADDD7] text-[#8A7D82] hover:text-[#5C4B51] transition-colors"
          data-testid="waitlist-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <WaitlistModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default WaitlistBanner;
